import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import {
  MessageSquare,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  CheckCheck,
  Clock,
  User,
  Package,
} from 'lucide-react';

interface ChatRequest {
  id: string;
  order_number: string;
  status: string;
  chat_authorized: boolean;
  chat_requested_at: string;
  chat_requested_by: string;
  requester_name: string;
  client_name: string;
  vendor_name: string;
  product_title: string;
}

export const ChatRequestsManager = () => {
  const [requests, setRequests] = useState<ChatRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      // Fetch orders that have a chat request pending
      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          status,
          chat_authorized,
          chat_requested_at,
          chat_requested_by,
          client:profiles!orders_client_id_fkey(full_name),
          vendor:profiles!orders_vendor_id_fkey(full_name),
          product:products(title)
        `)
        .not('chat_requested_at', 'is', null)
        .order('chat_requested_at', { ascending: false });

      if (error) throw error;

      const mapped: ChatRequest[] = (data || []).map((o: any) => ({
        id: o.id,
        order_number: o.order_number,
        status: o.status,
        chat_authorized: o.chat_authorized ?? false,
        chat_requested_at: o.chat_requested_at,
        chat_requested_by: o.chat_requested_by,
        requester_name: '',
        client_name: o.client?.full_name || 'Client inconnu',
        vendor_name: o.vendor?.full_name || 'Vendeur inconnu',
        product_title: o.product?.title || 'Produit',
      }));

      // Fetch requester names
      const requesterIds = [...new Set(mapped.map(r => r.chat_requested_by).filter(Boolean))];
      if (requesterIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', requesterIds);

        const profileMap = new Map((profiles || []).map(p => [p.id, p.full_name]));
        mapped.forEach(r => {
          r.requester_name = profileMap.get(r.chat_requested_by) || 'Utilisateur';
        });
      }

      setRequests(mapped);
    } catch (error) {
      console.error('Error fetching chat requests:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les demandes de chat',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAuthorize = async (orderIds: string[]) => {
    if (orderIds.length === 0) return;
    setProcessing(true);

    try {
      const { error } = await supabase
        .from('orders')
        .update({ chat_authorized: true } as any)
        .in('id', orderIds);

      if (error) throw error;

      toast({
        title: 'Chat autorisé',
        description: `${orderIds.length} conversation(s) autorisée(s)`,
      });

      setSelectedIds([]);
      fetchRequests();
    } catch (error) {
      console.error('Error authorizing chat:', error);
      toast({
        title: 'Erreur',
        description: "Impossible d'autoriser le chat",
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (orderIds: string[]) => {
    if (orderIds.length === 0) return;
    setProcessing(true);

    try {
      const { error } = await supabase
        .from('orders')
        .update({
          chat_authorized: false,
          chat_requested_at: null,
          chat_requested_by: null,
        } as any)
        .in('id', orderIds);

      if (error) throw error;

      toast({
        title: 'Demande refusée',
        description: `${orderIds.length} demande(s) de chat refusée(s)`,
      });

      setSelectedIds([]);
      fetchRequests();
    } catch (error) {
      console.error('Error rejecting chat:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de refuser la demande',
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const pendingIds = requests.filter(r => !r.chat_authorized).map(r => r.id);
    if (selectedIds.length === pendingIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pendingIds);
    }
  };

  const pendingRequests = requests.filter(r => !r.chat_authorized);
  const authorizedRequests = requests.filter(r => r.chat_authorized);

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}j`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <MessageSquare className="h-6 w-6" />
            Demandes de chat
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            {pendingRequests.length} demande(s) en attente · {authorizedRequests.length} autorisée(s)
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchRequests}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Bulk Actions */}
      {pendingRequests.length > 0 && (
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={selectedIds.length === pendingRequests.length && pendingRequests.length > 0}
                  onCheckedChange={toggleSelectAll}
                />
                <span className="text-sm text-muted-foreground">
                  {selectedIds.length > 0
                    ? `${selectedIds.length} sélectionnée(s)`
                    : 'Tout sélectionner'
                  }
                </span>
              </div>
              {selectedIds.length > 0 && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleAuthorize(selectedIds)}
                    disabled={processing}
                  >
                    {processing ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <CheckCheck className="h-4 w-4 mr-2" />
                    )}
                    Autoriser ({selectedIds.length})
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleReject(selectedIds)}
                    disabled={processing}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Refuser ({selectedIds.length})
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Requests */}
      {pendingRequests.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p>Aucune demande de chat en attente</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            En attente ({pendingRequests.length})
          </h3>
          {pendingRequests.map(req => (
            <Card key={req.id} className="hover:shadow-md transition-shadow">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    checked={selectedIds.includes(req.id)}
                    onCheckedChange={() => toggleSelect(req.id)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{req.order_number}</span>
                      <Badge variant="outline" className="text-xs">
                        {req.status}
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        <Clock className="h-3 w-3 mr-1" />
                        {timeAgo(req.chat_requested_at)}
                      </Badge>
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground space-y-0.5">
                      <p className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        Demandé par : <span className="font-medium text-foreground">{req.requester_name}</span>
                      </p>
                      <p className="flex items-center gap-1">
                        <Package className="h-3 w-3" />
                        {req.product_title} — {req.client_name} ↔ {req.vendor_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      onClick={() => handleAuthorize([req.id])}
                      disabled={processing}
                    >
                      <CheckCircle className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleReject([req.id])}
                      disabled={processing}
                    >
                      <XCircle className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Already Authorized */}
      {authorizedRequests.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            Autorisées ({authorizedRequests.length})
          </h3>
          {authorizedRequests.map(req => (
            <Card key={req.id} className="opacity-70">
              <CardContent className="py-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{req.order_number}</span>
                      <Badge variant="outline" className="text-xs">{req.status}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {req.client_name} ↔ {req.vendor_name} · {req.product_title}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-500 text-xs"
                    onClick={() => handleReject([req.id])}
                    disabled={processing}
                  >
                    Révoquer
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
